import type { Exercise, Question } from '../types.ts';
import { LessonBlocks } from './LessonBlocks.tsx';

export function ReadingPanel({
  exercise,
  question,
  full,
  onFull,
  onBack,
}: {
  exercise: Exercise;
  question?: Question;
  full: boolean;
  onFull: () => void;
  onBack: () => void;
}) {
  return (
    <div className="reader">
      <div className="reader-toolbar">
        <button className="text-button" onClick={onBack}>
          ← Back
        </button>
        {exercise.article ? (
          <a className="text-button" href={exercise.article.url} target="_blank" rel="noreferrer">
            Read on {exercise.article?.publisher ?? 'Wikipedia'} ↗
          </a>
        ) : (
          !full && (
            <button className="text-button" onClick={onFull}>
              View complete explanation ↗
            </button>
          )
        )}
      </div>
      <h1>{exercise.article ? exercise.title : exercise.reading.title}</h1>
      <ReadingBody exercise={exercise} sectionIds={!full ? question?.sectionIds : undefined} />
      {question && !full && (
        <div className="answer-explanation">
          <p>{question.prompt}</p>
          <p>
            <strong>Explanation. </strong>
            {question.explanation}
          </p>
        </div>
      )}
      {exercise.article && (
        <details className="reading-sources">
          <summary>Sources & notes</summary>
          <p>{exercise.reading.authorship}</p>
          <ul>
            {exercise.reading.sources.map((item) => (
              <li key={item.url}>
                <a href={item.url} target="_blank" rel="noreferrer">
                  {item.title} ↗
                </a>
                {item.note && <p>{item.note}</p>}
              </li>
            ))}
          </ul>
        </details>
      )}
      {question?.sourceSection && (
        <p className="source-section">
          <a href={question.sourceSection.url} target="_blank" rel="noreferrer">
            {exercise.article?.publisher ?? 'Wikipedia'}: {question.sourceSection.title} ↗
          </a>
        </p>
      )}
      {!full && !exercise.article && <button onClick={onFull}>View complete explanation</button>}
      <div className="reading-end">
        <button className="text-button" onClick={onBack}>
          ← Back
        </button>
      </div>
    </div>
  );
}

export function ReadingBody({
  exercise,
  sectionIds,
}: {
  exercise: Exercise;
  sectionIds?: string[];
}) {
  const sections = sectionIds
    ? exercise.reading.sections.filter((section) => sectionIds.includes(section.id))
    : exercise.reading.sections;
  return (
    <div className="reading-layout">
      <article className="reading-body">
        {sections.map((section, index) => (
          <section id={`section-${section.id}`} key={section.id}>
            {section.role && <p className="lesson-role">{section.role}</p>}
            <h2>
              <span>{String(index + 1).padStart(2, '0')}</span>
              {section.title}
            </h2>
            <LessonBlocks blocks={section.blocks} />
          </section>
        ))}
      </article>
      {!exercise.article && (
        <aside className="reading-aside">
          <p className="eyebrow">In this reading</p>
          <ol>
            {sections.map((section) => (
              <li key={section.id}>
                <a href={`#section-${section.id}`}>{section.title}</a>
              </li>
            ))}
          </ol>
          <details>
            <summary>Sources & notes</summary>
            <p>{exercise.reading.authorship}</p>
            {exercise.reading.sources.length ? (
              <ul>
                {exercise.reading.sources.map((source) => (
                  <li key={source.url}>
                    <a href={source.url} target="_blank" rel="noreferrer">
                      {source.title} ↗
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p>Original problems and explanations.</p>
            )}
          </details>
        </aside>
      )}
    </div>
  );
}
